/*
 * PANTOZA theme JavaScript
 *
 * Replaces the prototype's React state with small, dependency-free modules that work on
 * Shopify-rendered HTML:
 *   - sticky header, scroll reveals (IntersectionObserver), dialogs and drawers
 *   - real Shopify cart (AJAX Cart API + Section Rendering API)
 *   - product variants, gallery and quantity
 *   - predictive search, collection filtering/sorting, product recommendations
 *
 * All listeners are delegated from `document` and registered once, so sections that are
 * re-rendered by the theme editor or by the Section Rendering API never receive duplicates.
 */
(function () {
  'use strict';

  if (window.__pantozaThemeLoaded) return;
  window.__pantozaThemeLoaded = true;

  var config = window.PantozaTheme || {};
  var routes = config.routes || {};
  var strings = config.strings || {};
  var doc = document;

  function $(selector, scope) { return (scope || doc).querySelector(selector); }
  function $$(selector, scope) { return Array.prototype.slice.call((scope || doc).querySelectorAll(selector)); }
  function reducedMotion() { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  function parseHTML(html) { return new DOMParser().parseFromString(html, 'text/html'); }
  function format(template, count) { return String(template || '').replace('__count__', count); }

  /* ------------------------------------------------------------------ Toast */
  var toastTimer;
  function toast(message) {
    var el = $('[data-toast]');
    if (!el || !message) return;
    el.textContent = message;
    el.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.classList.remove('show'); }, 2800);
  }

  /* ---------------------------------------------------------- Sticky header */
  var scrollTicking = false;
  function updateHeader() {
    scrollTicking = false;
    var header = $('[data-header]');
    if (header) header.classList.toggle('scrolled', window.scrollY > 24);
  }
  window.addEventListener('scroll', function () {
    if (scrollTicking) return;
    scrollTicking = true;
    window.requestAnimationFrame(updateHeader);
  }, { passive: true });

  /* ----------------------------------------------------------- Scroll reveal */
  var revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.08 })
    : null;

  function initReveal(scope) {
    $$('.reveal:not(.visible)', scope).forEach(function (el) {
      if (revealObserver) revealObserver.observe(el);
      else el.classList.add('visible');
    });
  }

  /* ---------------------------------------------------- Dialogs and drawers */
  var FOCUSABLE = 'a[href], button:not(:disabled), input:not([type="hidden"]):not(:disabled), select:not(:disabled), textarea:not(:disabled), summary, [tabindex="0"]';

  var Modal = {
    active: null,

    open: function (id, trigger) {
      var el = doc.getElementById(id);
      if (!el) return;
      if (this.active && this.active.el === el) return;
      if (this.active) this.close(true);
      clearTimeout(el._closeTimer);
      el.classList.remove('closing');
      el.hidden = false;
      this.active = { el: el, trigger: trigger || doc.activeElement, overflow: doc.body.style.overflow };
      doc.body.style.overflow = 'hidden';
      setExpanded(id, true);
      el.dispatchEvent(new CustomEvent('modal:open', { bubbles: true }));
      var target = $('[data-autofocus]', el) || $('[data-modal-panel]', el);
      if (target) target.focus({ preventScroll: true });
    },

    close: function (immediate) {
      var state = this.active;
      if (!state) return;
      this.active = null;
      var el = state.el;
      doc.body.style.overflow = state.overflow;
      setExpanded(el.id, false);
      function finish() {
        el.hidden = true;
        el.classList.remove('closing');
        el.dispatchEvent(new CustomEvent('modal:close', { bubbles: true }));
      }
      if (immediate || reducedMotion() || !el.isConnected) {
        finish();
      } else {
        el.classList.add('closing');
        el._closeTimer = setTimeout(finish, 220);
      }
      if (!immediate && state.trigger && state.trigger.isConnected && state.trigger.focus) {
        state.trigger.focus({ preventScroll: true });
      }
    },

    trapFocus: function (event) {
      var state = this.active;
      if (!state) return;
      var panel = $('[data-modal-panel]', state.el);
      var items = $$(FOCUSABLE, panel).filter(function (node) { return node.getClientRects().length > 0; });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && (doc.activeElement === first || doc.activeElement === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (doc.activeElement === last || doc.activeElement === panel)) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  function setExpanded(id, value) {
    $$('[aria-controls="' + id + '"]').forEach(function (node) { node.setAttribute('aria-expanded', String(value)); });
  }

  doc.addEventListener('keydown', function (event) {
    if (!Modal.active) return;
    if (event.key === 'Escape') Modal.close();
    if (event.key === 'Tab') Modal.trapFocus(event);
  });

  /* ---------------------------------------------------- Button busy states */
  function setBusy(button, busy) {
    if (!button) return;
    button.setAttribute('aria-busy', String(busy));
    button.disabled = busy || button.hasAttribute('data-unavailable');
    var idle = $('[data-state="idle"]', button);
    var loading = $('[data-state="loading"]', button);
    if (idle && loading) {
      idle.hidden = busy;
      loading.hidden = !busy;
    }
  }

  /* -------------------------------------------------------------------- Cart */
  var Cart = {
    busy: false,

    sectionIds: function () {
      var ids = [];
      if ($('[data-cart-drawer]')) ids.push('cart-drawer');
      $$('[data-main-cart]').forEach(function (el) { ids.push(el.dataset.sectionId); });
      return ids;
    },

    render: function (sections) {
      if (!sections) return;
      Object.keys(sections).forEach(function (id) {
        var html = sections[id];
        if (!html) return;
        var fresh = parseHTML(html);
        var source = fresh.querySelector('[data-section-render="' + id + '"]');
        if (!source) return;
        $$('[data-section-render="' + id + '"]').forEach(function (target) {
          target.innerHTML = source.innerHTML;
          if (source.hasAttribute('data-cart-count-value')) {
            target.setAttribute('data-cart-count-value', source.getAttribute('data-cart-count-value'));
          }
        });
      });
      var countSource = $('[data-cart-count-value]');
      if (countSource) Cart.updateCount(countSource.getAttribute('data-cart-count-value'));
    },

    updateCount: function (count) {
      $$('[data-cart-count]').forEach(function (el) { el.textContent = count; });
      $$('[data-cart-toggle]').forEach(function (el) { el.setAttribute('aria-label', format(strings.bagLabel, count)); });
    },

    errorMessage: function (data) {
      if (!data) return strings.cartError;
      if (typeof data.description === 'string' && data.description) return data.description;
      if (typeof data.message === 'string' && data.message) return data.message;
      if (data.errors && typeof data.errors === 'string') return data.errors;
      return strings.cartError;
    },

    add: function (form, submitter) {
      if (form.dataset.pending === 'true') return;
      var button = submitter || $('[type="submit"]', form);
      if (button && button.disabled) return;
      var section = form.closest('[data-product-section]');
      var errorEl = section ? $('[data-form-error]', section) : null;

      form.dataset.pending = 'true';
      setBusy(button, true);
      if (errorEl && !errorEl.dataset.variantMessage) errorEl.hidden = true;

      var body = new FormData(form);
      var sections = Cart.sectionIds();
      if (sections.length) {
        body.append('sections', sections.join(','));
        body.append('sections_url', window.location.pathname);
      }

      fetch(routes.cartAdd + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: body
      })
        .then(function (response) {
          return response.json().catch(function () { return {}; }).then(function (data) {
            if (!response.ok || data.status) throw new Error(Cart.errorMessage(data));
            return data;
          });
        })
        .then(function (data) {
          if (config.cartType === 'page' || !$('[data-cart-drawer]')) {
            window.location.href = routes.cart;
            return;
          }
          Cart.render(data.sections);
          if (!$('[data-main-cart]')) Modal.open('CartDrawer', button);
        })
        .catch(function (error) {
          var message = error && error.message ? error.message : strings.cartError;
          if (errorEl) {
            errorEl.textContent = message;
            errorEl.hidden = false;
          }
          toast(message);
        })
        .finally(function () {
          delete form.dataset.pending;
          setBusy(button, false);
        });
    },

    change: function (key, quantity, origin) {
      if (Cart.busy) return;
      Cart.busy = true;
      var containers = $$('[data-section-render]');
      containers.forEach(function (el) { el.classList.add('is-loading'); });
      var focusAction = origin ? origin.getAttribute('data-cart-quantity') !== null ? 'quantity' : 'other' : 'other';

      fetch(routes.cartChange + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: Math.max(0, quantity),
          sections: Cart.sectionIds(),
          sections_url: window.location.pathname
        })
      })
        .then(function (response) {
          return response.json().catch(function () { return {}; }).then(function (data) {
            if (!response.ok || data.status) throw new Error(Cart.errorMessage(data));
            return data;
          });
        })
        .then(function (data) {
          Cart.render(data.sections);
          var line = $('[data-cart-line="' + cssEscape(key) + '"]', Modal.active ? Modal.active.el : doc);
          var focusTarget = line && focusAction === 'quantity' ? $('[data-cart-quantity-input]', line) : null;
          if (focusTarget) focusTarget.focus({ preventScroll: true });
          else if (Modal.active) $('[data-modal-panel]', Modal.active.el).focus({ preventScroll: true });
        })
        .catch(function (error) {
          var message = error && error.message ? error.message : strings.cartError;
          var line = $('[data-cart-line="' + cssEscape(key) + '"]');
          var lineError = line ? $('[data-line-error]', line) : null;
          if (lineError) {
            lineError.textContent = message;
            lineError.hidden = false;
          }
          toast(message);
          Cart.refresh();
        })
        .finally(function () {
          Cart.busy = false;
          $$('[data-section-render]').forEach(function (el) { el.classList.remove('is-loading'); });
        });
    },

    // Re-sync drawer/page markup with the server cart (e.g. after bfcache restore or an error).
    refresh: function () {
      var ids = Cart.sectionIds();
      if (!ids.length) return;
      fetch(window.location.pathname + '?sections=' + encodeURIComponent(ids.join(',')), { headers: { Accept: 'application/json' } })
        .then(function (response) { return response.ok ? response.json() : null; })
        .then(function (sections) { if (sections) Cart.render(sections); })
        .catch(function () {});
    }
  };

  function cssEscape(value) {
    return window.CSS && CSS.escape ? CSS.escape(value) : String(value).replace(/"/g, '\\"');
  }

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) Cart.refresh();
  });

  /* ------------------------------------------------------ Product page logic */
  function initProduct(section) {
    if (!section || section.dataset.productReady === 'true') return;
    section.dataset.productReady = 'true';
    var jsonEl = $('[data-product-json]', section);
    if (!jsonEl) return;
    var data;
    try { data = JSON.parse(jsonEl.textContent); } catch (error) { return; }
    section._productData = data;
  }

  function selectedOptions(section) {
    return $$('[data-option-index]', section).map(function (fieldset) {
      var checked = $('input:checked', fieldset);
      return checked ? checked.value : null;
    });
  }

  function onVariantChange(section) {
    initProduct(section);
    var data = section._productData;
    if (!data) return;
    var options = selectedOptions(section);
    var variant = data.variants.find(function (candidate) {
      return candidate.options.every(function (value, index) { return value === options[index]; });
    });

    // Option labels and per-value availability (given the other selected options)
    $$('[data-option-index]', section).forEach(function (fieldset, optionIndex) {
      var label = $('[data-option-value]', fieldset);
      if (label) label.textContent = options[optionIndex] || '';
      $$('input[data-option-input]', fieldset).forEach(function (input) {
        var available = data.variants.some(function (candidate) {
          return candidate.available && candidate.options[optionIndex] === input.value && candidate.options.every(function (value, index) {
            return index === optionIndex || value === options[index];
          });
        });
        var optionLabel = input.nextElementSibling;
        if (!optionLabel) return;
        optionLabel.classList.toggle('unavailable', !available);
        var small = $('small', optionLabel);
        if (small) small.hidden = available;
      });
    });

    var idInput = $('[data-variant-id]', section);
    var button = $('[data-add-button]', section);
    var addLabel = $('[data-add-label]', section);
    var addIcon = $('[data-add-icon]', section);
    var errorEl = $('[data-form-error]', section);
    var price = $('[data-price]', section);
    var canBuy = Boolean(variant && variant.available);

    if (idInput) idInput.value = variant ? variant.id : '';
    if (button) {
      button.toggleAttribute('data-unavailable', !canBuy);
      button.disabled = !canBuy;
    }
    if (addLabel) addLabel.textContent = !variant ? strings.unavailable : variant.available ? strings.addToBag : strings.soldOut;
    if (addIcon) addIcon.hidden = !canBuy;
    if (price && variant) price.innerHTML = variant.price;
    if (errorEl) {
      errorEl.textContent = canBuy ? '' : strings.variantUnavailable;
      errorEl.hidden = canBuy;
      if (canBuy) delete errorEl.dataset.variantMessage;
      else errorEl.dataset.variantMessage = 'true';
    }
    if (variant) {
      if (variant.media) showMedia(section, String(variant.media));
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState(window.history.state, '', url.toString());
    }
  }

  function showMedia(section, mediaId) {
    var slides = $$('[data-media-id]', section);
    if (!slides.some(function (slide) { return slide.dataset.mediaId === mediaId; })) return;
    var grid = $('[data-gallery][data-layout="grid"]', section);
    if (grid) {
      // Grid layout keeps every image visible: scroll the slider (mobile) or the page (desktop) to it.
      var target = slides.filter(function (slide) { return slide.dataset.mediaId === mediaId; })[0];
      var track = target.parentElement;
      if (track.scrollWidth > track.clientWidth + 2) track.scrollTo({ left: target.offsetLeft - track.offsetLeft, behavior: reducedMotion() ? 'auto' : 'smooth' });
      else if (target !== slides[0]) target.scrollIntoView({ block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
      $$('[data-thumb]', section).forEach(function (thumb) { thumb.classList.toggle('selected', thumb.dataset.thumb === mediaId); });
      return;
    }
    slides.forEach(function (slide) {
      var active = slide.dataset.mediaId === mediaId;
      if (!active) {
        $$('video', slide).forEach(function (video) { video.pause(); });
      }
      slide.hidden = !active;
    });
    $$('[data-thumb]', section).forEach(function (thumb) {
      var active = thumb.dataset.thumb === mediaId;
      thumb.classList.toggle('selected', active);
      if (active) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
  }

  /* ------------------------------------------------------- Predictive search */
  var search = { timer: null, controller: null, initialHTML: new WeakMap() };

  function runSearch(input) {
    var modal = input.closest('[data-modal]') || doc;
    var body = $('[data-search-body]', modal);
    var status = $('[data-search-status]', modal);
    var clear = $('[data-search-clear]', modal);
    var query = input.value.trim();
    if (clear) clear.hidden = input.value.length === 0;
    if (!body) return;
    if (!search.initialHTML.has(body)) search.initialHTML.set(body, body.innerHTML);

    clearTimeout(search.timer);
    if (search.controller) search.controller.abort();

    if (!query) {
      body.innerHTML = search.initialHTML.get(body);
      if (status) status.textContent = '';
      return;
    }

    search.timer = setTimeout(function () {
      search.controller = 'AbortController' in window ? new AbortController() : null;
      var url = routes.predictiveSearch + '?q=' + encodeURIComponent(query) +
        '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last&section_id=predictive-search';
      fetch(url, { signal: search.controller ? search.controller.signal : undefined })
        .then(function (response) {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then(function (html) {
          var fresh = parseHTML(html).querySelector('[data-search-body]');
          if (!fresh) throw new Error('missing');
          body.innerHTML = fresh.innerHTML;
          var hint = $('[data-search-empty-hint]', body);
          if (hint) hint.textContent = body.getAttribute('data-search-empty-text') || '';
          var counter = $('[data-search-count]', body);
          if (status && counter) status.textContent = counter.getAttribute('data-search-count') + ' ' + (counter.getAttribute('data-search-count') === '1' ? 'result' : 'results');
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          body.innerHTML = '<p class="eyebrow search-label"></p><div class="search-footer"><a class="text-link"></a></div>';
          body.querySelector('.search-label').textContent = strings.searchError;
          var link = body.querySelector('a');
          link.href = routes.search + '?type=product&q=' + encodeURIComponent(query);
          link.textContent = query;
        });
    }, 250);
  }

  /* --------------------------------------------- Collection filters + sorting */
  var facetHistory = false;
  var facetController = null;
  var facetTimer = null;

  function facetUrl(form) {
    var params = new URLSearchParams();
    new FormData(form).forEach(function (value, key) {
      if (value !== '') params.append(key, value);
    });
    var action = form.getAttribute('action') || window.location.pathname;
    var query = params.toString();
    return action + (query ? '?' + query : '');
  }

  function applyFacets(form) {
    var url = facetUrl(form);
    var sectionId = form.dataset.sectionId;
    var section = doc.querySelector('[data-facets-section][data-section-id="' + sectionId + '"]');
    if (!section || !window.fetch) {
      window.location.href = url;
      return;
    }
    if (facetController) facetController.abort();
    facetController = 'AbortController' in window ? new AbortController() : null;
    var results = $('[data-facet-results]', section);
    if (results) results.classList.add('is-loading');

    fetch(url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=' + encodeURIComponent(sectionId), {
      signal: facetController ? facetController.signal : undefined
    })
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(function (html) {
        var fresh = parseHTML(html);
        var freshResults = fresh.querySelector('[data-facet-results]');
        if (!freshResults || !results) throw new Error('missing');
        results.innerHTML = freshResults.innerHTML;
        ['[data-results-count]', '[data-filter-show-label]'].forEach(function (selector) {
          var source = fresh.querySelector(selector);
          $$(selector).forEach(function (target) { if (source) target.textContent = source.textContent; });
        });
        window.history.pushState({ facets: true }, '', url);
        facetHistory = true;
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        window.location.href = url;
      })
      .finally(function () {
        if (results) results.classList.remove('is-loading');
      });
  }

  window.addEventListener('popstate', function (event) {
    if (facetHistory || (event.state && event.state.facets)) window.location.reload();
  });

  // The filter form lives in the desktop sidebar; on mobile it is moved into the drawer.
  doc.addEventListener('modal:open', function (event) {
    var drawer = event.target;
    if (!drawer.hasAttribute('data-filter-drawer')) return;
    var form = doc.getElementById(drawer.dataset.formId);
    var panel = $('[data-modal-panel]', drawer);
    var showButton = panel ? $('.button', panel) : null;
    if (form && panel && showButton) panel.insertBefore(form, showButton);
  });
  doc.addEventListener('modal:close', function (event) {
    var drawer = event.target;
    if (!drawer.hasAttribute('data-filter-drawer')) return;
    var form = doc.getElementById(drawer.dataset.formId);
    var section = drawer.closest('[data-facets-section]');
    var aside = section ? $('[data-facet-aside]', section) : null;
    if (form && aside && !aside.contains(form)) aside.appendChild(form);
  });

  /* ------------------------------------------------- Product recommendations */
  function initRecommendations(scope) {
    $$('[data-recommendations]:not([data-loaded])', scope).forEach(function (el) {
      el.setAttribute('data-loaded', 'true');
      var url = el.getAttribute('data-url');
      if (!url) return;
      fetch(url)
        .then(function (response) { return response.ok ? response.text() : ''; })
        .then(function (html) {
          if (!html) return;
          var fresh = parseHTML(html).querySelector('[data-recommendations]');
          if (fresh && fresh.innerHTML.trim()) {
            el.innerHTML = fresh.innerHTML;
            initReveal(el);
          }
        })
        .catch(function () {});
    });
  }

  /* ----------------------------------------------------- Delegated listeners */
  doc.addEventListener('click', function (event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    var opener = target.closest('[data-modal-open]');
    if (opener) {
      event.preventDefault();
      Modal.open(opener.getAttribute('data-modal-open'), opener);
      return;
    }

    if (target.closest('[data-modal-close]')) {
      event.preventDefault();
      Modal.close();
      return;
    }

    if (target.hasAttribute('data-modal') && Modal.active && Modal.active.el === target) {
      Modal.close();
      return;
    }

    var stepButton = target.closest('[data-quantity-step]');
    if (stepButton) {
      var wrapper = stepButton.closest('.quantity');
      var input = wrapper ? $('[data-quantity-input]', wrapper) : null;
      if (input) {
        var step = Number(stepButton.getAttribute('data-quantity-step'));
        var min = Number(input.min) || 1;
        var max = input.max ? Number(input.max) : Infinity;
        input.value = Math.min(max, Math.max(min, (Number(input.value) || min) + step));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    var cartQuantity = target.closest('[data-cart-quantity]');
    if (cartQuantity) {
      event.preventDefault();
      Cart.change(cartQuantity.getAttribute('data-key'), Number(cartQuantity.getAttribute('data-cart-quantity')), cartQuantity);
      return;
    }

    var remove = target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      Cart.change(remove.getAttribute('data-key'), 0, remove);
      return;
    }

    var thumb = target.closest('[data-thumb]');
    if (thumb) {
      var section = thumb.closest('[data-product-section]');
      if (section) showMedia(section, thumb.getAttribute('data-thumb'));
      return;
    }

    var clear = target.closest('[data-search-clear]');
    if (clear) {
      var form = clear.closest('form');
      var searchInput = form ? $('[data-search-input]', form) : null;
      if (searchInput) {
        searchInput.value = '';
        runSearch(searchInput);
        searchInput.focus();
      }
    }
  });

  // "Sign in" buttons open Shopify's account sheet from the header instead of leaving the store.
  doc.addEventListener('click', function (event) {
    var opener = event.target.closest && event.target.closest('[data-open-account]');
    if (!opener) return;
    var account = $('shopify-account.header-account');
    var avatar = account && account.querySelector('[slot="signed-out-avatar"]');
    if (!avatar || !window.customElements || !customElements.get('shopify-account')) return;
    event.preventDefault();
    avatar.click();
  });

  doc.addEventListener('submit', function (event) {
    var form = event.target;
    if (form.matches('[data-product-form]')) {
      event.preventDefault();
      Cart.add(form, event.submitter);
      return;
    }
    if (form.matches('[data-newsletter-form]')) {
      var email = $('input[type="email"]', form);
      var status = $('[data-newsletter-status]', form);
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim())) {
        event.preventDefault();
        email.setAttribute('aria-invalid', 'true');
        if (status) {
          status.textContent = strings.emailInvalid;
          status.classList.add('form-error');
        }
        email.focus();
      }
    }
  });

  doc.addEventListener('input', function (event) {
    var target = event.target;
    if (target.matches('[data-search-input]')) {
      runSearch(target);
      return;
    }
    if (target.closest && target.closest('[data-newsletter-form]') && target.type === 'email') {
      target.removeAttribute('aria-invalid');
      var status = $('[data-newsletter-status]', target.form);
      if (status && status.classList.contains('form-error')) {
        status.classList.remove('form-error');
        status.textContent = status.getAttribute('data-default-text') || '';
      }
    }
  });

  doc.addEventListener('change', function (event) {
    var target = event.target;

    if (target.matches('[data-option-input]')) {
      var section = target.closest('[data-product-section]');
      if (section) onVariantChange(section);
      return;
    }

    if (target.matches('[data-cart-quantity-input]')) {
      Cart.change(target.getAttribute('data-key'), Number(target.value), target);
      return;
    }

    if (target.matches('[data-facet-input]') && target.form) {
      clearTimeout(facetTimer);
      var facetForm = target.form;
      facetTimer = setTimeout(function () { applyFacets(facetForm); }, 120);
      return;
    }

    if (target.matches('[data-localization-select]') && target.form) {
      target.form.submit();
    }
  });

  /* ------------------------------------------- Smooth accordions (FAQ rows) */
  doc.addEventListener('click', function (event) {
    var summary = event.target.closest && event.target.closest('[data-accordion] > summary');
    if (!summary || reducedMotion()) return;
    var details = summary.parentElement;
    var body = $('[data-accordion-body]', details);
    if (!body || !body.animate) return;
    event.preventDefault();
    if (details.dataset.animating) return;
    details.dataset.animating = 'true';
    if (details.open) {
      var start = body.offsetHeight;
      details.classList.add('is-closing');
      body.animate([{ height: start + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 260, easing: 'ease' }).onfinish = function () {
        details.open = false;
        details.classList.remove('is-closing');
        delete details.dataset.animating;
      };
    } else {
      details.open = true;
      var end = body.offsetHeight;
      body.animate([{ height: '0px', opacity: 0 }, { height: end + 'px', opacity: 1 }], { duration: 300, easing: 'ease' }).onfinish = function () {
        delete details.dataset.animating;
      };
    }
  });

  /* ----------------------------------------- Footer: mobile accordions, back to top */
  var footerQuery = window.matchMedia('(max-width: 700px)');
  function syncFooterColumns() {
    $$('[data-footer-col]').forEach(function (col) { col.open = !footerQuery.matches; });
  }
  if (footerQuery.addEventListener) footerQuery.addEventListener('change', syncFooterColumns);
  doc.addEventListener('click', function (event) {
    var summary = event.target.closest && event.target.closest('[data-footer-col] > summary');
    if (summary && !footerQuery.matches) { event.preventDefault(); return; }
    var top = event.target.closest && event.target.closest('[data-back-to-top]');
    if (top) {
      window.scrollTo({ top: 0, behavior: reducedMotion() ? 'auto' : 'smooth' });
      var skip = $('#MainContent');
      if (skip && skip.focus) skip.focus({ preventScroll: true });
    }
  });

  /* ---------------------------------------------------- Theme editor support */
  doc.addEventListener('shopify:section:load', function (event) {
    syncFooterColumns();
    initReveal(event.target);
    initRecommendations(event.target);
    updateHeader();
  });
  doc.addEventListener('shopify:section:unload', function (event) {
    if (Modal.active && event.target.contains(Modal.active.el)) Modal.close(true);
  });
  doc.addEventListener('shopify:section:select', function (event) {
    if ($('[data-cart-drawer]', event.target)) Modal.open('CartDrawer');
  });
  doc.addEventListener('shopify:section:deselect', function (event) {
    if (Modal.active && event.target.contains(Modal.active.el)) Modal.close();
  });

  /* ------------------------------------------------------------------- Init */
  function init() {
    syncFooterColumns();
    updateHeader();
    initReveal(doc);
    initRecommendations(doc);
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init);
  else init();

  window.Pantoza = { Modal: Modal, Cart: Cart, toast: toast };
})();
